import React, { useEffect, useState, useRef } from "react";
import api from "../api/client";
import { getLoggedInUser } from "../utils/auth";

export default function SupervisorAssignPage() {
  /* ================= USER ================= */
  const user = getLoggedInUser() || {};

  const supervisorCode =
    user.userId || user.UserID || user.username || user.UserName || "";

  const [supervisorName, setSupervisorName] = useState(
    user.username || user.UserName || ""
  );

  const role = user?.role || user?.Role || "";
  const ROLE_TEXT =
    role === "SupervisorCumEmployee" ? "SUPERVISOR + EMPLOYEE" : "SUPERVISOR";

  /* ================= STATE ================= */
  const [employees, setEmployees] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);

  const [employeeCode, setEmployeeCode] = useState("");
  const [employeeName, setEmployeeName] = useState("");

  const [selectedItems, setSelectedItems] = useState([]);

  const [searchOrder, setSearchOrder] = useState("");
  const scanRef = useRef(null);

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    if (supervisorCode) loadSupervisor();
    loadEmployees();
    loadOrders();
    setTimeout(() => scanRef.current?.focus(), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supervisorCode]);

  /* ================= API LOADERS ================= */
  const loadSupervisor = async () => {
    try {
      const res = await api.get(`/api/pickup/user/${supervisorCode}`);
      if (res.data?.empName) setSupervisorName(res.data.empName);
    } catch (err) {
      console.error("Supervisor load error:", err);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await api.get("/api/pickup/employees/free");
      setEmployees(res.data || []);
    } catch (err) {
      console.error("Employees load error:", err);
    }
  };

  const loadOrders = async () => {
    try {
      const res = await api.get("/api/pickup/orders/pending");
      const mapped = (res.data || []).map((o) => ({
        ...o,
        uiStatus: "Pending",
      }));
      setOrders(mapped);
      setFilteredOrders(mapped);
    } catch (err) {
      console.error("Orders load error:", err);
    }
  };

  /* ================= SEARCH ORDER ================= */
  const handleOrderSearch = (value) => {
    const cleaned = value.trim().replace(/[\r\n\t ]+/g, "");
    setSearchOrder(cleaned);

    if (!cleaned) {
      setFilteredOrders(orders);
      return;
    }

    const filtered = orders.filter((o) =>
      o.orderId.toLowerCase().includes(cleaned.toLowerCase())
    );

    setFilteredOrders(filtered);
  };

  /* ================= EMPLOYEE SELECT ================= */
  const handleEmployeeChange = (e) => {
    const code = e.target.value;
    setEmployeeCode(code);

    // ✅ Self assignment
    if (code === supervisorCode) {
      setEmployeeName(`${supervisorName} (Self)`);
      return;
    }

    const emp = employees.find((x) => x.userId === code);
    setEmployeeName(emp?.empName || "");
  };

  /* ================= ASSIGN ================= */
  const handleAssign = async () => {
    if (!employeeCode || selectedItems.length === 0) {
      alert("Please select employee and at least one SKU");
      return;
    }

    try {
      await api.post("/api/pickup/orders/assign-bulk", {
        employeeId: employeeCode,
        assignedBy: supervisorCode,
        items: selectedItems,
      });

      alert("SKUs assigned successfully.");

      setEmployeeCode("");
      setEmployeeName("");
      setSelectedItems([]);
      setSearchOrder("");

      await loadEmployees();
      await loadOrders();
    } catch (err) {
      console.error("Assign Error:", err);
      alert("Error assigning order");
    }
  };

  /* ================= RESET ================= */
  const handleReset = () => {
    setEmployeeCode("");
    setEmployeeName("");
    setSelectedItems([]);
    setSearchOrder("");
    setFilteredOrders(orders);
    setTimeout(() => scanRef.current?.focus(), 200);
  };

  /* ================= UI ================= */
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.heading}>Order Pickup – {ROLE_TEXT}</h2>

        <div style={styles.sectionTitle}>Supervisor Information</div>
        <label style={styles.label}>Supervisor Code</label>
        <input style={styles.input} value={supervisorCode} disabled />

        <label style={styles.label}>Supervisor Name</label>
        <input style={styles.input} value={supervisorName} disabled />

        <label style={styles.label}>Role</label>
        <input style={styles.input} value={ROLE_TEXT} disabled />

        <div style={styles.sectionTitle}>Assign To Employee</div>
        <label style={styles.label}>Employee Code</label>
        <select
          style={styles.input}
          value={employeeCode}
          onChange={handleEmployeeChange}
        >
          <option value="">Select Employee</option>

          {/* ✅ Allow self assignment for SupervisorCumEmployee */}
          {role === "SupervisorCumEmployee" && supervisorCode && (
            <option value={supervisorCode}>
              {supervisorCode} – {supervisorName} (Self)
            </option>
          )}

          {employees.map((e) => (
            <option key={e.userId} value={e.userId}>
              {e.userId} – {e.empName}
            </option>
          ))}
        </select>

        <label style={styles.label}>Employee Name</label>
        <input style={styles.input} value={employeeName} disabled />

        <div style={styles.sectionTitle}>Scan / Search Order ID</div>
        <input
          ref={scanRef}
          style={styles.input}
          placeholder="Scan Order ID or type manually"
          value={searchOrder}
          onChange={(e) => handleOrderSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleOrderSearch(e.target.value)}
        />

        <div style={styles.sectionTitle}>Pending Orders</div>
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th>Select</th>
                <th>Order ID</th>
                <th>SKU</th>
                <th>Pieces</th>
                <th>Shipper</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((o, idx) => (
                <tr
                  key={idx}
                  style={
                    selectedItems.some(
                      (i) => i.orderId === o.orderId && i.sku === o.sku
                    )
                      ? styles.rowSelected
                      : undefined
                  }
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedItems.some(
                        (i) => i.orderId === o.orderId && i.sku === o.sku
                      )}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems((prev) => [
                            ...prev,
                            { orderId: o.orderId, sku: o.sku },
                          ]);
                        } else {
                          setSelectedItems((prev) =>
                            prev.filter(
                              (i) =>
                                !(
                                  i.orderId === o.orderId && i.sku === o.sku
                                )
                            )
                          );
                        }
                      }}
                    />
                  </td>
                  <td>{o.orderId}</td>
                  <td>{o.sku}</td>
                  <td>{o.requestedPieces}</td>
                  <td>
                    {o.shipperId} – {o.shipperName}
                  </td>
                  <td>{o.uiStatus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.buttonContainer}>
          <button style={styles.mainButton} onClick={handleAssign}>
            Assign Order
          </button>
          <button style={styles.resetButton} onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */
const styles = {
  page: { background: "#f5f6fa", minHeight: "100vh", padding: 20 },
  card: {
    maxWidth: 900,
    margin: "0 auto",
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
  },
  heading: { textAlign: "center", color: "#b00000", marginBottom: 20 },
  sectionTitle: {
    marginTop: 16,
    marginBottom: 6,
    fontWeight: "bold",
    borderBottom: "1px solid #ddd",
  },
  label: { fontSize: 14, marginTop: 8 },
  input: {
    width: "98%",
    padding: 8,
    marginTop: 4,
    marginBottom: 6,
    borderRadius: 6,
    border: "1px solid #aaa",
  },
  tableWrapper: {
    marginTop: 10,
    maxHeight: 220,
    overflowY: "auto",
    border: "1px solid #ddd",
    borderRadius: 8,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  rowSelected: { background: "#ffe5e5" },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    gap: 16,
    marginTop: 20,
  },
  mainButton: {
    width: 180,
    padding: 12,
    background: "#b00000",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 16,
    fontWeight: "bold",
  },
  resetButton: {
    width: 180,
    padding: 12,
    background: "#444",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: "bold",
  },
};
